// ============================================
// FOOD COMMUNITY SERVICE - Frontend
// ============================================

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const getAuthHeader = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = {
  get: (url: string) => axios.get(`${API_URL}/api${url}`, { headers: getAuthHeader() }),
  post: (url: string, data?: any) => axios.post(`${API_URL}/api${url}`, data, { headers: getAuthHeader() }),
  put: (url: string, data?: any) => axios.put(`${API_URL}/api${url}`, data, { headers: getAuthHeader() }),
  delete: (url: string) => axios.delete(`${API_URL}/api${url}`, { headers: getAuthHeader() }),
};

export interface RecipeUser {
  id: string;
  firstName: string;
  lastName: string;
  profilePhotoUrl?: string;
  userType?: string;
}

export interface Ingredient {
  name: string;
  amount: string;
  unit?: string;
}

export interface RecipePost {
  id: string;
  userId: string;
  user: RecipeUser | null;
  title: string;
  description?: string;
  imageUrl?: string;
  prepTime?: number;
  servings?: number;
  calories?: number;
  protein?: number;
  carbs?: number;
  fats?: number;
  ingredients: Ingredient[];
  instructions?: string;
  tags: string[];
  customTags: string[];
  mealType?: string;
  isValidatedByNutritionist: boolean;
  validatedBy?: { id: string; firstName: string; lastName: string } | null;
  likesCount: number;
  savesCount: number;
  commentsCount: number;
  repostsCount: number;
  averageRating: number;
  ratingsCount: number;
  viewsCount: number;
  isLiked: boolean;
  isSaved: boolean;
  userRating?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeComment {
  id: string;
  postId: string;
  userId: string;
  user: RecipeUser;
  content: string;
  likesCount: number;
  isLiked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeCollection {
  id: string;
  userId: string;
  name: string;
  description?: string;
  coverImageUrl?: string;
  isDefault: boolean;
  itemsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DietTag {
  value: string;
  label: string;
  color: string;
}

export interface FeedPagination {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface FeedResponse {
  posts: RecipePost[];
  pagination: FeedPagination;
}

export type DateFilter = 'today' | 'yesterday' | 'week' | 'all';

const TAG_LABELS: Record<string, string> = {
  HIGH_PROTEIN: '#highprotein',
  LOW_CALORIE: '#lowcalorie',
  LOW_CARB: '#lowcarb',
  VEGAN: '#vegan',
  VEGETARIAN: '#vegetarian',
  GLUTEN_FREE: '#faragluten',
  DAIRY_FREE: '#faralactate',
  ANTI_INFLAMMATORY: '#antiinflamator',
  WEIGHT_LOSS: '#slabit',
  MUSCLE_GAIN: '#masamusculara',
  KETO: '#keto',
  MEDITERRANEAN: '#mediteranean',
  HIGH_FIBER: '#highfiber',
  LOW_FAT: '#lowfat',
  BALANCED: '#echilibrat',
};

const TAG_COLORS: Record<string, string> = {
  HIGH_PROTEIN: 'bg-green-100 text-green-700',
  LOW_CALORIE: 'bg-blue-100 text-blue-700',
  LOW_CARB: 'bg-orange-100 text-orange-700',
  VEGAN: 'bg-emerald-100 text-emerald-700',
  VEGETARIAN: 'bg-lime-100 text-lime-700',
  GLUTEN_FREE: 'bg-yellow-100 text-yellow-700',
  DAIRY_FREE: 'bg-sky-100 text-sky-700',
  ANTI_INFLAMMATORY: 'bg-purple-100 text-purple-700',
  WEIGHT_LOSS: 'bg-red-100 text-red-700',
  MUSCLE_GAIN: 'bg-amber-100 text-amber-700',
  KETO: 'bg-yellow-100 text-yellow-800',
  MEDITERRANEAN: 'bg-cyan-100 text-cyan-700',
  HIGH_FIBER: 'bg-teal-100 text-teal-700',
  LOW_FAT: 'bg-pink-100 text-pink-700',
  BALANCED: 'bg-indigo-100 text-indigo-700',
};

export function getTagLabel(tag: string): string {
  return TAG_LABELS[tag] || `#${tag.toLowerCase()}`;
}

export function getTagColor(tag: string): string {
  return TAG_COLORS[tag] || 'bg-gray-100 text-gray-700';
}

export function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Acum';
  if (diffMins < 60) return `Acum ${diffMins} min`;
  if (diffHours < 24) return `Acum ${diffHours}h`;
  if (diffDays === 1) return 'Ieri';
  if (diffDays < 7) return `Acum ${diffDays} zile`;
  return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
}

export const foodCommunityService = {
  // ==================== FEED ====================

  async getFeed(params: {
    page?: number;
    limit?: number;
    dateFilter?: DateFilter;
    tags?: string[];
    mealType?: string;
  } = {}): Promise<FeedResponse> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.dateFilter) query.set('dateFilter', params.dateFilter);
    if (params.tags?.length) params.tags.forEach(t => query.append('tags', t));
    if (params.mealType) query.set('mealType', params.mealType);

    const res = await api.get(`/food-community/feed?${query}`);
    return res.data;
  },

  async getTrending(limit = 20): Promise<{ posts: RecipePost[] }> {
    const res = await api.get(`/food-community/trending?limit=${limit}`);
    return res.data;
  },

  async getForYou(limit = 20): Promise<{ posts: RecipePost[] }> {
    const res = await api.get(`/food-community/for-you?limit=${limit}`);
    return res.data;
  },

  async getStoriesBar(): Promise<{ users: RecipeUser[] }> {
    const res = await api.get('/food-community/stories');
    return res.data;
  },

  async getTags(): Promise<{ tags: DietTag[] }> {
    const res = await api.get('/food-community/tags');
    return res.data;
  },

  // ==================== POSTS ====================

  async createPost(data: {
    title: string;
    description?: string;
    imageUrl?: string;
    prepTime?: number;
    servings?: number;
    calories?: number;
    protein?: number;
    carbs?: number;
    fats?: number;
    ingredients?: Ingredient[];
    instructions?: string;
    tags?: string[];
    customTags?: string[];
    mealType?: string;
  }): Promise<RecipePost> {
    const res = await api.post('/food-community/posts', data);
    return res.data;
  },

  async getPost(postId: string): Promise<RecipePost> {
    const res = await api.get(`/food-community/posts/${postId}`);
    return res.data;
  },

  async updatePost(postId: string, data: Partial<RecipePost>): Promise<RecipePost> {
    const res = await api.put(`/food-community/posts/${postId}`, data);
    return res.data;
  },

  async deletePost(postId: string): Promise<void> {
    await api.delete(`/food-community/posts/${postId}`);
  },

  // ==================== LIKES & SAVES ====================

  async toggleLike(postId: string): Promise<{ liked: boolean; saved: boolean }> {
    const res = await api.post(`/food-community/posts/${postId}/like`);
    return res.data;
  },

  async toggleSave(postId: string, collectionId?: string): Promise<{ saved: boolean }> {
    const res = await api.post(`/food-community/posts/${postId}/save`, { collectionId });
    return res.data;
  },

  // ==================== COMMENTS ====================

  async getComments(postId: string, page = 1, limit = 20): Promise<{
    comments: RecipeComment[];
    pagination: FeedPagination;
  }> {
    const res = await api.get(`/food-community/posts/${postId}/comments?page=${page}&limit=${limit}`);
    return res.data;
  },

  async addComment(postId: string, content: string): Promise<RecipeComment> {
    const res = await api.post(`/food-community/posts/${postId}/comments`, { content });
    return res.data;
  },

  async deleteComment(commentId: string): Promise<void> {
    await api.delete(`/food-community/comments/${commentId}`);
  },

  async toggleCommentLike(commentId: string): Promise<{ liked: boolean }> {
    const res = await api.post(`/food-community/comments/${commentId}/like`);
    return res.data;
  },

  // ==================== RATINGS ====================

  async ratePost(postId: string, rating: number, review?: string): Promise<{
    rating: number;
    averageRating: number;
  }> {
    const res = await api.post(`/food-community/posts/${postId}/rate`, { rating, review });
    return res.data;
  },

  // ==================== REPOSTS ====================

  async repost(postId: string, imageUrl?: string, note?: string): Promise<any> {
    const res = await api.post(`/food-community/posts/${postId}/repost`, { imageUrl, note });
    return res.data;
  },

  // ==================== COLLECTIONS ====================

  async getCollections(): Promise<{ collections: RecipeCollection[] }> {
    const res = await api.get('/food-community/collections');
    return res.data;
  },

  async createCollection(name: string, description?: string): Promise<RecipeCollection> {
    const res = await api.post('/food-community/collections', { name, description });
    return res.data;
  },

  async getCollectionItems(collectionId: string, page = 1, limit = 20): Promise<{
    collection: RecipeCollection;
    posts: RecipePost[];
    pagination: FeedPagination;
  }> {
    const res = await api.get(`/food-community/collections/${collectionId}?page=${page}&limit=${limit}`);
    return res.data;
  },

  async deleteCollection(collectionId: string): Promise<void> {
    await api.delete(`/food-community/collections/${collectionId}`);
  },

  async getSavedPosts(page = 1, limit = 20): Promise<FeedResponse> {
    const res = await api.get(`/food-community/saved?page=${page}&limit=${limit}`);
    return res.data;
  },

  // ==================== USER POSTS ====================

  async getUserPosts(userId: string, page = 1, limit = 20): Promise<FeedResponse> {
    const res = await api.get(`/food-community/users/${userId}/posts?page=${page}&limit=${limit}`);
    return res.data;
  },

  // ==================== SEARCH ====================

  async search(q: string, page = 1, limit = 20): Promise<FeedResponse> {
    const res = await api.get(`/food-community/search?q=${encodeURIComponent(q)}&page=${page}&limit=${limit}`);
    return res.data;
  },

  // ==================== HELPERS ====================

  getTagLabel,
  getTagColor,
  formatTimeAgo,
};
